// How many tasks carry exactly one ID, asked of the tasks themselves.
//
// The search index answers "what looks like this": it tolerates typos, so
// typing L748474 lists L744474 and L743474, and it can be behind. On
// 2026-10-11 the search server refused new tasks for hours, and a task that
// existed was not found by its own ID. This asks Firestore, with the same two
// queries /tasks/by-id lists from, as count aggregations: 2 document reads
// however many tasks match.
import { useEffect, useState } from 'react';
import { getFirestore, collection, query, getCountFromServer } from 'firebase/firestore';
import app from '../firebase';
import { ID_FIELDS, baseConstraints } from './useTaskIdHistory';

/**
 * `{ atLeast, exact }` for one ID in one workspace.
 *
 * `exact` is null when both field names matched something: a task carrying
 * the ID under both is in both counts, so their sum may count it twice.
 * `atLeast` is always safe to act on.
 */
export async function countTasksWithId(subscriberId, value) {
  const wanted = String(value ?? '').trim();
  if (!subscriberId || !wanted) return { atLeast: 0, exact: 0 };
  const db = getFirestore(app);
  const counts = await Promise.all(ID_FIELDS.map(async (field) => {
    const snap = await getCountFromServer(query(
      collection(db, 'tasks'),
      ...baseConstraints(subscriberId, field, wanted),
    ));
    return snap.data().count;
  }));
  const nonEmpty = counts.filter(c => c > 0);
  return {
    atLeast: Math.max(0, ...counts),
    exact: nonEmpty.length > 1 ? null : (nonEmpty[0] || 0),
  };
}

/**
 * The count for one ID, or null while it is unknown. A refused or failed
 * count stays null: the caller shows nothing, it does not show an error.
 */
export default function useTaskIdCount({ subscriberId, value }) {
  const [counted, setCounted] = useState(null);

  useEffect(() => {
    let live = true;
    setCounted(null);
    if (!subscriberId || !value) return undefined;
    countTasksWithId(subscriberId, value)
      .then((result) => { if (live) setCounted(result); })
      .catch(() => { /* a customer session may not count across the workspace */ });
    return () => { live = false; };
  }, [subscriberId, value]);

  return counted;
}
